'use client';
import React from 'react';
import Image from 'next/image';
import VideoComponent from './VideoComponent';
import { revealed } from '@/lib/animation/revealed';

export default function UGCGallery({ block, position }) {
  const {
    heading,
    cards,
    uid,
    text_color,
    card_background_color,
    background_color,
    video_autoplay,
    animations_enabled
  } = block.ugc_gallery.reference[0];
  // Limit to 5 cards only
  const visibleCards = cards.slice(0, 5);

  return (
    <section
      id={`${uid}_${position}`}
      className={`bg-[${background_color}] overflow-hidden text-white lg:m-5`}
      data-name="ugc_gallery"
    >
      <div className="mx-auto flex w-90 max-w-full flex-col items-center justify-center gap-10 px-6 py-30 sm:w-192 sm:gap-20 sm:px-15 sm:py-20 lg:w-full lg:max-w-350 lg:gap-20 lg:px-0 lg:py-20">
        <div className="flex w-full flex-col items-center justify-center gap-5 lg:max-w-310">
          <revealed.div
            from="bottom"
            fade={true}
            delayMs={100}
            animate={animations_enabled}
            className="h3 sm:h2 lg:h1 flex items-center justify-center text-center"
          >
            {heading}
          </revealed.div>
        </div>

        <div className="flex w-full justify-center px-6 sm:px-0 lg:px-20">
          <div className="grid grid-cols-1 justify-items-center gap-5 sm:grid-cols-2 sm:gap-5 lg:w-full lg:max-w-310 lg:grid-cols-12 lg:justify-items-stretch lg:gap-30 xl:gap-10">
            {visibleCards.map((card, index) => (
              <revealed.div
                key={index}
                from="bottom"
                fade={true}
                delayMs={250 + index * 250}
                rootMargin="0px 0px 5% 0px"
                animate={animations_enabled}
                className={getCardClasses(index)}
              >
                <UGCCard
                  key={`card-${index}`}
                  card={card}
                  cardIndex={index}
                  cardClasses={getCardClasses(index)}
                  cardBackgroundColor={card_background_color}
                  textColor={text_color}
                  uid={uid}
                  videoAutoplay={video_autoplay}
                />
              </revealed.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function UGCCard({
  card,
  cardIndex,
  cardClasses,
  cardBackgroundColor,
  textColor,
  uid,
  videoAutoplay
}) {
  return (
    <div
      className={`${cardClasses} ${card.description ? `bg-[${cardBackgroundColor}]` : ''} group relative flex`}
    >
      {card.video_url ? (
        <>
          <VideoComponent
            card={card}
            cardIndex={cardIndex}
            enableAutoplayTimer={videoAutoplay}
          />

          {/* Description overlay */}
          {card.description && (
            <div className="absolute right-0 bottom-0 left-0 bg-black/50 backdrop-blur-sm">
              <div className={`h5 p-4 text-center text-${textColor}`}>
                {card.description}
              </div>
            </div>
          )}
        </>
      ) : (
        <div className="flex h-full w-full flex-col items-center justify-center gap-10 p-5 px-10">
          {card.image &&
            (() => {
              const imageWidth = card.image.dimension?.width || 400;
              const imageHeight = card.image.dimension?.height || 400;
              const aspectRatio = imageWidth / imageHeight;
              const imageSize = getImageSize(aspectRatio);

              return (
                <div
                  className="relative"
                  style={{
                    width: imageSize.width,
                    height: imageSize.height,
                    aspectRatio: imageSize.ratio
                  }}
                >
                  <Image
                    id={uid + '_' + cardIndex}
                    src={card.image.url}
                    alt={
                      card.image.alt ||
                      card.image.title?.replace(/\.[^/.]+$/, '') ||
                      ''
                    }
                    width={imageWidth}
                    height={imageHeight}
                    sizes="(min-width:1024px) 240px, (min-width:768px) 200px, 180px"
                    className="h-full w-full object-contain"
                  />
                </div>
              );
            })()}
          {card.description && (
            <div className="xl:h4 h5 text-center text-white">
              {card.description}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

const getImageSize = (aspectRatio) => {
  if (aspectRatio > 4) {
    return { width: '15rem', height: '2.75rem', ratio: '60/11' };
  } else if (aspectRatio > 2) {
    return { width: '6rem', height: '2.182rem', ratio: '11/4' };
  } else {
    // Default for new image types
    return { width: '7.5rem', height: '7.5rem', ratio: '1/1' };
  }
};

const getCardClasses = (cardIndex) => {
  switch (cardIndex) {
    case 0:
      return 'h-[19.063rem] w-78 overflow-hidden sm:h-auto sm:w-full sm:max-w-[20.563rem] sm:aspect-[329/579] lg:col-start-1 lg:col-span-4 lg:row-start-1 lg:row-span-4 lg:max-w-162 lg:aspect-[387/440]';
    case 1:
      return 'h-[39.375rem] w-78 overflow-hidden sm:h-auto sm:w-full sm:max-w-[20.563rem] sm:aspect-[329/579] lg:col-start-5 lg:col-span-4 lg:row-start-1 lg:row-span-6 lg:max-w-162 lg:aspect-[387/680]';
    case 2:
      return 'h-[39.375rem] w-78 overflow-hidden sm:h-auto sm:w-full sm:max-w-[20.563rem] sm:aspect-[329/579] lg:col-start-9 lg:col-span-4 lg:row-start-1 lg:row-span-6 lg:max-w-162 lg:aspect-[387/680]';
    case 3:
      return 'h-[39.375rem] w-78 overflow-hidden sm:h-auto sm:w-full sm:max-w-[20.563rem] sm:aspect-[329/579] lg:col-start-1 lg:col-span-4 lg:row-start-5 lg:row-span-6 lg:max-w-162 lg:aspect-[387/680]';
    case 4:
      return 'h-[32.625rem] w-78 overflow-hidden sm:col-span-2 sm:h-auto sm:w-full sm:max-w-[40.563rem] sm:aspect-[649/342] lg:col-start-5 lg:col-span-8 lg:row-start-7 lg:row-span-4 lg:max-w-none lg:aspect-[813/440]';
    default:
      return 'hidden';
  }
};
